import { useFloorReplacementProcess } from 'dataQueries/photoStudio.query'

import { JobStatus } from 'constants/API'
import { JobType, useAIVisualsResult, useJobPoll } from 'dataQueries'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from 'utils/logging'
import { PhotoStudioGenerationStepId } from '../generationSteps'
import { FLOOR_TEMPLATES } from '../VirtualStaging/components/FloorTemplatePicker/FloorTemplatePicker.component'

interface UseFloorReplacementProcessorProps {
  assignmentId: string
  sourceVisualFilename: string
  getLatestPreviewFilename: () => string | null
  onProgressUpdate?: (step: string) => void
  onComplete?: (resultUrl: string) => void
  onError?: (error: any) => void
}

/**
 * Reusable Floor Replacement processor hook
 * Handles the complete flow from processing to result display
 */
export const useFloorReplacementProcessor = ({
  assignmentId,
  sourceVisualFilename,
  getLatestPreviewFilename,
  onProgressUpdate,
  onComplete,
  onError
}: UseFloorReplacementProcessorProps) => {
  const floorReplacementProcess = useFloorReplacementProcess()
  const floorReplacementJobPoll = useJobPoll(JobType.FLOOR_REPLACEMENT)
  const floorReplacementResult = useAIVisualsResult() // Reuse the same result endpoint
  const { t } = useTranslation(['photo_studio_editing'])

  const processFloorReplacement = useCallback(async (
    floorTemplate: string,
    previousJobId?: string
  ) => {
    try {
      onProgressUpdate?.(PhotoStudioGenerationStepId.START_PROCESSING)

      // Step 1: Start Floor Replacement processing
      const currentPreview = getLatestPreviewFilename()
      const inputVisualFilename = currentPreview || sourceVisualFilename

      // Map template ID to API name
      const selectedTemplate = FLOOR_TEMPLATES.find((t) => t.id === floorTemplate)
      if (!selectedTemplate) {
        throw new Error(`Invalid floor template: ${floorTemplate}`)
      }

      const processResponse = await floorReplacementProcess.mutateAsync({
        assignmentId,
        visualFilename: inputVisualFilename,
        floorTemplate: selectedTemplate.apiName,
      })

      onProgressUpdate?.(PhotoStudioGenerationStepId.UPLOAD_MASK)

      // Step 2: Poll job until completion
      const jobResult = await floorReplacementJobPoll.mutateAsync({
        jobId: processResponse.id
      })

      if (jobResult.status === JobStatus.FINISHED) {
        onProgressUpdate?.(PhotoStudioGenerationStepId.PROCESSING_COMPLETE)

        // Step 3: Get the AI result
        const aiResult = await floorReplacementResult.refetchWithParams(assignmentId, processResponse.id)

        if (aiResult.signedURL) {
          onComplete?.(aiResult.signedURL)
          return {
            success: true,
            resultUrl: aiResult.signedURL,
            jobId: processResponse.id
          }
        } else {
          throw new Error('No AI result available')
        }
      } else if (jobResult.status === JobStatus.FAILED) {
        // Throw error for failed Floor Replacement job - toast will be shown by controller's onError callback
        throw new Error(t('virtual_staging_error'))
      } else {
        throw new Error(`Floor Replacement job failed with status: ${jobResult.status}`)
      }
    } catch (error) {
      logger.error('Floor Replacement: Processing failed', error, {
        context: {
          component: 'PhotoStudio',
          action: 'floorReplacementProcess',
          metadata: {
            assignmentId,
            floorTemplate,
            sourceVisualFilename,
          }
        }
      })
      onError?.(error)

      return {
        success: false,
        error
      }
    }
  }, [
    assignmentId,
    sourceVisualFilename,
    getLatestPreviewFilename,
    floorReplacementProcess,
    floorReplacementJobPoll,
    floorReplacementResult,
    onProgressUpdate,
    onComplete,
    onError,
    t
  ])

  return {
    processFloorReplacement,
    isProcessing: floorReplacementProcess.isPending || floorReplacementJobPoll.isPending,
    error: floorReplacementProcess.error || floorReplacementJobPoll.error
  }
}
